import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Pencil, Send, Ban, Power, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ModuleShell, StatusBadge, KV } from "@/components/partners/common";
import PartnerForm from "@/components/partners/PartnerForm";
import {
  ContactsTab, AddressesTab, BankTab, DocumentsTab, TransactionsTab, OutstandingTab, PerformanceTab,
  SupplierItemsTab, DealerPricingTab, AuditTab, ApprovalsTab,
} from "@/components/partners/ProfileTabs";
import { KIND, fetchMeta, fmtMoney, fmtDate, titleCase, apiError } from "@/lib/partnerApi";

export default function PartnerProfile({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const k = KIND[kind];
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [statusDlg, setStatusDlg] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { setData((await api.get(`${k.api}/${id}`)).data); } catch (e) { toast.error(apiError(e, "Not found")); nav(k.base); }
  }, [k.api, k.base, id, nav]);
  useEffect(() => { fetchMeta(kind).then(setMeta).catch(() => {}); }, [kind]);
  useEffect(() => { load(); }, [load]);

  if (!data || !meta) return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  const p = data.partner;
  const h = data.header;
  const can = (a) => data.actions.includes(a);
  const com = p.commercial || {};
  const fin = can("view_financial");

  const submit = async () => {
    try { await api.post(`${k.api}/${id}/submit`); toast.success("Submitted for approval"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const changeStatus = async () => {
    try {
      await api.post(`${k.api}/${id}/status`, { status: statusDlg, reason });
      toast.success(`Status changed to ${titleCase(statusDlg)}`); setStatusDlg(null); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete draft ${p.code}? This is only allowed for never-approved records with no transactions.`)) return;
    try { await api.delete(`${k.api}/${id}`); toast.success("Deleted"); nav(k.base); } catch (e) { toast.error(apiError(e)); }
  };

  const editable = p.approval_status !== "pending";
  const TABS = [
    ["overview", "Overview"], ["contacts", "Contacts"], ["addresses", "Addresses"], ["tax", "Tax Details"],
    ["bank", "Bank Details"], ["commercial", "Commercial Terms"], ["items", "Items & Pricing"], ["documents", "Documents"],
    ["transactions", "Transactions"], ...(fin ? [["outstanding", "Outstanding"]] : []), ["performance", "Performance"],
    ["approvals", "Approvals"], ["audit", "Audit History"],
  ];
  const tc = { kind, pid: id, meta, canEdit: can("edit") && editable, onChanged: load };

  return (
    <ModuleShell kind={kind} tid={`${kind}-profile`} title={p.name}
      subtitle={<span className="inline-flex items-center gap-2"><Link to={k.base} className="text-blue-600 hover:underline inline-flex items-center"><ArrowLeft className="w-3.5 h-3.5 mr-0.5" />{k.plural}</Link> · {p.legal_name || titleCase(p.partner_type)}</span>}
      actions={(
        <>
          {can("edit") && editable && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-partner-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>}
          {can("edit") && ["draft", "rejected"].includes(p.approval_status) && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={submit} data-testid="submit-approval-btn"><Send className="w-4 h-4 mr-1" /> Submit for Approval</Button>
          )}
          {p.approval_status === "approved" && p.master_status !== "active" && (can("approve") || can("block")) && (
            <Button size="sm" variant="outline" onClick={() => setStatusDlg("active")}><CheckCircle2 className="w-4 h-4 mr-1" /> Activate</Button>
          )}
          {p.master_status === "active" && can("deactivate") && <Button size="sm" variant="outline" onClick={() => setStatusDlg("inactive")}><Power className="w-4 h-4 mr-1" /> Deactivate</Button>}
          {p.master_status !== "blocked" && can("block") && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setStatusDlg("blocked")} data-testid="block-btn"><Ban className="w-4 h-4 mr-1" /> Block</Button>}
          {can("delete") && ["draft", "rejected"].includes(p.approval_status) && <Button size="sm" variant="ghost" onClick={remove}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
        </>
      )}
    >
      {/* Header strip: Code | Name | Status | Contact | Credit Limit | Outstanding | Available Credit | Last Transaction | Assigned Employee */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-4" data-testid="profile-header">
        <KV label="Code" value={<span className="mono font-bold">{h.code}</span>} />
        <KV label="Name" value={h.name} />
        <KV label="Status" value={<span className="flex gap-1 flex-wrap"><StatusBadge value={h.status} tid="header-status" /><StatusBadge value={h.approval_status} /></span>} />
        <KV label="Contact" value={h.contact ? <span>{h.contact.name}<span className="block text-[11px] text-slate-500">{h.contact.mobile || h.contact.email}</span></span> : null} />
        <KV label="Credit Limit" value={fin ? (h.credit_limit ? fmtMoney(h.credit_limit) : "Not set") : "—"} />
        <KV label="Outstanding" value={fin ? fmtMoney(h.outstanding) : "—"} />
        <KV label="Available Credit" value={fin ? (h.available_credit === null ? "—" : <span className={h.available_credit === 0 ? "text-red-600 font-semibold" : ""}>{fmtMoney(h.available_credit)}</span>) : "—"} />
        <KV label="Last Transaction" value={h.last_transaction ? <span>{h.last_transaction.number}<span className="block text-[11px] text-slate-500">{h.last_transaction.doc_type} · {fmtDate(h.last_transaction.date)}</span></span> : null} />
        <KV label={kind === "dealer" ? "Salesperson" : "Buyer"} value={h.assigned_employee} />
      </div>

      {p.status_reason && ["blocked", "inactive"].includes(p.master_status) && (
        <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2">{titleCase(p.master_status)}: {p.status_reason}</div>
      )}
      {data.completeness.length > 0 && ["draft", "rejected"].includes(p.approval_status) && (
        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 flex gap-2" data-testid="completeness">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Complete before submitting for approval: {data.completeness.join(" · ")}</div>
        </div>
      )}
      {data.pending_approvals.length > 0 && (
        <div className="text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded-md px-3 py-2">
          Awaiting approval: {data.pending_approvals.map((a) => `${a.code} (${titleCase(a.type)} — ${a.levels[a.current_level - 1].name})`).join(", ")}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto justify-start bg-slate-100">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Section title="Basic Information">
              <KV label={`${k.label} Type`} value={titleCase(p.partner_type)} /><KV label="Category" value={p.category} />
              <KV label="Short Name" value={p.short_name} /><KV label="Priority" value={titleCase(p.priority)} />
              <KV label="Registration Date" value={fmtDate(p.registration_date)} /><KV label="Website" value={p.website} />
              <KV label={kind === "dealer" ? "Parent Dealer" : "Parent Company"} value={data.parent ? <Link className="text-blue-600 hover:underline" to={`${k.base}/${data.parent.id}`}>{data.parent.code} · {data.parent.name}</Link> : null} />
              {kind === "supplier" ? <KV label="Supplier Rating" value={p.rating ? `${p.rating} / 5` : null} /> : <>
                <KV label="Territory / Region" value={p.territory} /><KV label="Sales Zone" value={p.sales_zone} />
                <KV label="Price Level" value={titleCase(p.price_level)} /></>}
            </Section>
            <Section title="Key Terms">
              <KV label="Payment Terms" value={titleCase(com.payment_terms)} /><KV label="Currency" value={com.currency} />
              {kind === "supplier" ? <><KV label="Lead Time" value={com.lead_time_days ? `${com.lead_time_days} days` : null} /><KV label="MOQ" value={com.moq} /></>
                : <><KV label="Credit Days" value={com.credit_days} /><KV label="Discount %" value={com.discount_pct} /></>}
              <KV label="Agreement" value={com.agreement_number} /><KV label="Agreement Ends" value={fmtDate(com.agreement_end)} />
            </Section>
            <Section title="Record">
              <KV label="Created" value={`${fmtDate(p.created_at)} · ${p.created_by || "—"}`} />
              <KV label="Last Modified" value={p.updated_at ? `${fmtDate(p.updated_at)} · ${p.updated_by}` : null} />
              <KV label="Approved" value={p.approved_at ? `${fmtDate(p.approved_at)} · ${p.approved_by}` : null} />
              <KV label="Transactions" value={Object.values(data.transaction_counts).reduce((a, b) => a + b, 0)} />
              {p.performance?.performance_score != null && <KV label="Performance Score" value={p.performance.performance_score} />}
              {p.duplicate_authorized && <KV label="Duplicate authorized" value={`${p.duplicate_authorized.by}: ${p.duplicate_authorized.reason}`} />}
            </Section>
            {Object.keys(p.custom_fields || {}).length > 0 && (
              <Section title="Additional Fields">
                {(meta.custom_fields || []).map((cf) => <KV key={cf.key} label={cf.label} value={String(p.custom_fields?.[cf.key] ?? "")} />)}
              </Section>
            )}
            {p.notes && <Section title="Notes"><div className="col-span-2 text-sm text-slate-700 whitespace-pre-wrap">{p.notes}</div></Section>}
          </div>
        </TabsContent>
        <TabsContent value="contacts" className="mt-4"><ContactsTab {...tc} /></TabsContent>
        <TabsContent value="addresses" className="mt-4"><AddressesTab {...tc} /></TabsContent>
        <TabsContent value="tax" className="mt-4">
          <Section title="Tax & Statutory" cols={4}>
            <KV label="Tax Registration Country" value={p.tax_registration_country || "India"} /><KV label="GST Registration Type" value={titleCase(p.gst_registration_type)} />
            <KV label="GSTIN" value={p.gstin} mono /><KV label="GST State" value={p.gstin ? meta.gst_state_codes?.[p.gstin.slice(0, 2)] : null} />
            <KV label="PAN" value={p.pan} mono /><KV label="TAN" value={p.tan} mono /><KV label="CIN" value={p.cin} mono />
            <KV label="MSME No." value={p.msme_number} mono /><KV label="Udyam No." value={p.udyam_number} mono />
            {kind === "supplier" ? <><KV label="TDS Applicable" value={p.tds_applicable ? "Yes" : "No"} /><KV label="TDS Category" value={p.tds_category} /></>
              : <KV label="Tax Category" value={p.tax_category} />}
            <KV label="Tax Identification No." value={p.tax_identification_number} /><KV label="Tax Exemption" value={p.tax_exemption_details} />
          </Section>
          <p className="text-xs text-slate-500 mt-2">Tax / exemption certificates are uploaded on the Documents tab. Per-country tax fields are configurable in Settings.</p>
        </TabsContent>
        <TabsContent value="bank" className="mt-4"><BankTab {...tc} /></TabsContent>
        <TabsContent value="commercial" className="mt-4">
          <Section title="Commercial Terms" cols={4}>
            {Object.entries(com).length === 0 && <div className="text-sm text-slate-500">No commercial terms set.</div>}
            {Object.entries(com).filter(([, v]) => v !== null && v !== "").map(([key, v]) => (
              <KV key={key} label={titleCase(key)} value={key === "credit_limit" ? fmtMoney(v) : (key.includes("date") || (key.startsWith("agreement_") && key !== "agreement_number")) ? fmtDate(v) : titleCase(String(v))} />
            ))}
          </Section>
          {p.financials_hidden && <p className="text-xs text-slate-500 mt-2">Credit and pricing terms are hidden for your role.</p>}
        </TabsContent>
        <TabsContent value="items" className="mt-4">
          {kind === "supplier" ? <SupplierItemsTab pid={id} canEdit={tc.canEdit} canPrice={can("change_pricing")} />
            : fin ? <DealerPricingTab pid={id} meta={meta} canPrice={can("change_pricing")} partner={p} /> : <div className="text-sm text-slate-500">Pricing is visible to roles with financial access.</div>}
        </TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsTab {...tc} canApprove={can("approve")} /></TabsContent>
        <TabsContent value="transactions" className="mt-4"><TransactionsTab kind={kind} pid={id} /></TabsContent>
        {fin && <TabsContent value="outstanding" className="mt-4"><OutstandingTab kind={kind} pid={id} meta={meta} /></TabsContent>}
        <TabsContent value="performance" className="mt-4"><PerformanceTab kind={kind} pid={id} canEdit={can("edit")} currency={p?.commercial?.currency || "INR"} /></TabsContent>
        <TabsContent value="approvals" className="mt-4"><ApprovalsTab kind={kind} pid={id} onChanged={load} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab kind={kind} pid={id} /></TabsContent>
      </Tabs>

      <PartnerForm kind={kind} meta={meta} open={editOpen} onOpenChange={setEditOpen} initial={p} onSaved={load} />
      <Dialog open={!!statusDlg} onOpenChange={(v) => !v && setStatusDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{statusDlg === "active" ? "Activate" : statusDlg === "blocked" ? "Block" : "Deactivate"} {p.code}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {statusDlg === "blocked" ? (kind === "dealer" ? "A blocked dealer cannot place new orders." : "No new POs can be raised on a blocked supplier.")
              : statusDlg === "inactive" ? "Inactive records stay in history and reports but drop out of pickers." : "The record becomes usable on transactions again."}
          </p>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required to block / deactivate)" data-testid="status-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDlg(null)}>Cancel</Button>
            <Button onClick={changeStatus} className={statusDlg === "blocked" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"} data-testid="status-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ModuleShell>
  );
}

function Section({ title, children, cols = 2 }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-sm font-semibold text-slate-700 mb-3">{title}</div>
      <div className={`grid gap-3 ${cols === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>{children}</div>
    </div>
  );
}
